import { useState } from "react";
import { CalendarIcon, X } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import { Input } from "~/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";

const DISPLAY_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

/** "Aug 1, 2026", or "" for an unset or invalid date. */
function formatDisplay(date: Date | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", DISPLAY_OPTIONS);
}

/**
 * Parses free text a user typed. Returns undefined for empty or unparseable
 * text so a half-typed date never commits a bogus value. Month-name and slash
 * forms parse in local time; only a bare ISO `YYYY-MM-DD` would be read as UTC,
 * and this field never displays that form.
 */
function parseTyped(text: string): Date | undefined {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

const clearLabel = (name: string | undefined) => (name ? `Clear ${name}` : "Clear date");
const openLabel = (name: string | undefined) =>
  name ? `Open calendar for ${name}` : "Open calendar";

export interface DatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  /**
   * Render a typeable text field beside the calendar. Without it the trigger is
   * a single button that shows the selected date and opens the calendar.
   */
  withInput?: boolean;
  /** Show a clear affordance that resets the value to undefined. */
  clearable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Accessible name, used when the picker has no visible label of its own. */
  "aria-label"?: string;
  id?: string;
  className?: string;
}

export function DatePicker(props: DatePickerProps) {
  return props.withInput ? <DatePickerWithInput {...props} /> : <DatePickerButton {...props} />;
}

function DatePickerButton({
  value,
  onChange,
  clearable = false,
  placeholder = "Pick a date",
  disabled = false,
  id,
  className,
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const showClear = clearable && !!value && !disabled;

  return (
    <div className={cn("relative", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            disabled={disabled}
            aria-label={ariaLabel}
            className={cn(
              "w-full justify-start gap-2 font-normal",
              !value && "text-muted-foreground",
              showClear && "pr-8"
            )}
          >
            <CalendarIcon className="size-4 shrink-0 opacity-70" />
            <span className="truncate">{value ? formatDisplay(value) : placeholder}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            defaultMonth={value}
            autoFocus
            onSelect={(date) => {
              onChange(date);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
      {showClear && (
        <ClearButton
          label={clearLabel(ariaLabel)}
          onClick={() => onChange(undefined)}
          className="absolute top-1/2 right-1 -translate-y-1/2"
        />
      )}
    </div>
  );
}

function DatePickerWithInput({
  value,
  onChange,
  clearable = false,
  placeholder,
  disabled = false,
  id,
  className,
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(() => formatDisplay(value));

  // Reformat the field from the committed value whenever that value changes
  // from the outside, but never mid-edit, so typing is not clobbered. React's
  // "adjust state during render" pattern, keyed on the date's time so a fresh
  // Date instance for the same day does not force a reset.
  const valueTime = value?.getTime() ?? null;
  const [syncedTime, setSyncedTime] = useState(valueTime);
  if (!focused && valueTime !== syncedTime) {
    setSyncedTime(valueTime);
    setText(formatDisplay(value));
  }

  const showClear = clearable && !!value && !disabled;

  // The field commits on blur and Enter rather than on every keystroke: a
  // partially typed date parses to real but wrong days, and this field drives a
  // server-side query that should not fire on those.
  const commitTyped = () => {
    const parsed = parseTyped(text);
    setText(formatDisplay(parsed));
    onChange(parsed);
  };

  return (
    <div className={cn("relative", className)}>
      <Input
        id={id}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={ariaLabel}
        className={cn(showClear ? "pr-14" : "pr-9")}
        onChange={(e) => setText(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          commitTyped();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitTyped();
          } else if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      />
      <div className="absolute top-1/2 right-1 flex -translate-y-1/2 items-center gap-0.5">
        {showClear && (
          <ClearButton label={clearLabel(ariaLabel)} onClick={() => onChange(undefined)} />
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={disabled}
              aria-label={openLabel(ariaLabel)}
              className="size-6 text-muted-foreground hover:text-foreground"
            >
              <CalendarIcon className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={value}
              defaultMonth={value}
              autoFocus
              onSelect={(date) => {
                onChange(date);
                setText(formatDisplay(date));
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

interface ClearButtonProps {
  label: string;
  onClick: () => void;
  className?: string;
}

function ClearButton({ label, onClick, className }: ClearButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        "flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-foreground",
        className
      )}
    >
      <X className="size-3.5" />
    </button>
  );
}
